import { ReactNode } from 'react';

import { TopicCategory } from '@dreryk/egabinet-visit-frontend-api';

import { Translate, useFormController } from '@dreryk/react-common';
import { ActionInput, HeartIcon, StandardTabs } from '@dreryk/react-components';

import { documentTemplateActionTranslationKeys } from '../../../../translations';

import { StyledDocumentTemplateActionForm, StyledTabLabel } from './DocumentTemplateActionForm.styled';
import { DocumentTemplateTable } from './table/DocumentTemplateTable';

const FAVORITES_TAB_ID = 'FAVORITES';
type DocumentTemplateTabId = typeof FAVORITES_TAB_ID | TopicCategory;

type DocumentTemplateActionFormBean = {
   searchText: string | null;
};

type DocumentTemplateActionFormProps = {
   readonly visitId: number;
   readonly children: ReactNode | ReactNode[];
   readonly onDocumentTemplateOpen: VoidFunction;
};

const TOPIC_CATEGORY_TABS: TopicCategory[] = [
   'REFERRALS',
   'CERTIFICATES',
   'ASSESSMENTS',
   'EXAMINATION_CARDS',
   'TESTS_AND_SCALES',
   'OTHER',
];

export const DocumentTemplateActionForm = (props: DocumentTemplateActionFormProps): JSX.Element => {
   const { register, watch } = useFormController<DocumentTemplateActionFormBean>({
      searchText: null,
   });

   const searchText = watch('searchText');
   const isSearching = Boolean(searchText);

   return (
      <StyledDocumentTemplateActionForm noValidate autoComplete={'off'}>
         <ActionInput
            controller={register('searchText')}
            testId={'searchText'}
            placeholder={documentTemplateActionTranslationKeys.form.actionInput.placeholder}
            autoFocus
         />
         {isSearching ? (
            <DocumentTemplateTable
               visitId={props.visitId}
               searchText={searchText}
               topicCategory={null}
               favorite={false}
               onDocumentTemplateOpen={props.onDocumentTemplateOpen}
            />
         ) : (
            <StandardTabs<DocumentTemplateTabId> testId={'documentTemplateCategories'}>
               {[
                  <StandardTabs.Tab<DocumentTemplateTabId>
                     key={FAVORITES_TAB_ID}
                     id={FAVORITES_TAB_ID}
                     testId={FAVORITES_TAB_ID}
                     label={
                        <StyledTabLabel>
                           <HeartIcon />
                           <Translate>{documentTemplateActionTranslationKeys.form.tabsMap.FAVORITES}</Translate>
                        </StyledTabLabel>
                     }
                  >
                     <DocumentTemplateTable
                        visitId={props.visitId}
                        searchText={searchText}
                        topicCategory={null}
                        favorite
                        onDocumentTemplateOpen={props.onDocumentTemplateOpen}
                     />
                  </StandardTabs.Tab>,
                  ...TOPIC_CATEGORY_TABS.map((topicCategory) => (
                     <StandardTabs.Tab<DocumentTemplateTabId>
                        key={topicCategory}
                        id={topicCategory}
                        testId={topicCategory}
                        label={
                           <StyledTabLabel>
                              <Translate>{documentTemplateActionTranslationKeys.form.tabsMap[topicCategory]}</Translate>
                           </StyledTabLabel>
                        }
                     >
                        <DocumentTemplateTable
                           visitId={props.visitId}
                           searchText={searchText}
                           topicCategory={topicCategory}
                           favorite={false}
                           onDocumentTemplateOpen={props.onDocumentTemplateOpen}
                        />
                     </StandardTabs.Tab>
                  )),
               ]}
            </StandardTabs>
         )}
         {props.children}
      </StyledDocumentTemplateActionForm>
   );
};
